import React, { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import { OutlinedInputRightAdornment } from "@assets/TextField";
import { DefaultLabel } from "@assets/label";
import { HomeType } from "./adTypeList";

// --- URL 파싱 헬퍼 ---
const parseScreenUrl = (url) => {
  if (url && url.startsWith("fever://")) {
    return url.replace("fever://", "");
  }
  return "";
};

const parseHomeUrl = (url) => {
  if (!url) return { homeType: "store", params: {} };

  if (url.startsWith("fever.store:///")) {
    const match = url.match(/productNo=([^&]*)/);
    return {
      homeType: "store",
      params: { productNo: match?.[1] || "" },
    };
  }
  if (url.startsWith("open.community://")) {
    const postMatch = url.match(/post=([^&]*)/);
    const boardMatch = url.match(/board=([^&]*)/);
    const filterMatch = url.match(/filter=([^&]*)/);

    if (postMatch && !boardMatch) {
      return {
        homeType: "community_post",
        params: { post: postMatch[1] },
      };
    }
    if (boardMatch) {
      return {
        homeType: "community_board",
        params: {
          board: boardMatch[1],
          filter: filterMatch?.[1] || "-1",
        },
      };
    }
  }
  return { homeType: "store", params: {} };
};

// --- URL 생성 헬퍼 ---
const generateHomeUrl = (homeType, params) => {
  switch (homeType) {
    case "store":
      return params.productNo
        ? `fever.store:///product-detail?productNo=${params.productNo}`
        : "";
    case "community_board":
      return params.board
        ? `open.community://key=MAIN&board=${params.board}&filter=${params.filter || "-1"}`
        : "";
    case "community_post":
      return params.post
        ? `open.community://key=MAIN&post=${params.post}`
        : "";
    default:
      return "";
  }
};

/**
 * 오픈 방식별 URL 입력 컴포넌트
 *
 * @param {string}   openType        - 현재 오픈 방식 (W|B|S|H|N)
 * @param {Array}    openTypeList    - 오픈 방식 옵션 리스트
 * @param {string}   linkUrl         - Android URL (link_url)
 * @param {string}   linkUrlI        - iOS URL (link_url_i)
 * @param {function} onOpenTypeChange - 오픈 방식 변경 콜백 (value)
 * @param {function} onUrlChange      - URL 변경 콜백 (linkUrl, linkUrlI)
 * @param {boolean}  disabled         - 비활성화 여부
 * @param {string}   urlLabel         - URL 라벨 (기본: "URL")
 */
const UrlInputByOpenType = ({
  openType,
  openTypeList,
  linkUrl,
  linkUrlI,
  onOpenTypeChange,
  onUrlChange,
  disabled = false,
  urlLabel = "URL",
}) => {
  // 스크린 모드 로컬 상태
  const [screenName, setScreenName] = useState("");
  // 홈 모드 로컬 상태
  const [homeType, setHomeType] = useState("store");
  const [homeParams, setHomeParams] = useState({});

  // 초기 URL에서 로컬 상태 복원
  useEffect(() => {
    if (openType === "S" && linkUrl) {
      setScreenName(parseScreenUrl(linkUrl));
    } else if (openType === "H" && linkUrl) {
      const parsed = parseHomeUrl(linkUrl);
      setHomeType(parsed.homeType);
      setHomeParams(parsed.params);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 오픈 방식 변경 시 로컬 상태 리셋 (URL 초기화는 부모에서 처리)
  const handleOpenTypeChange = useCallback(
    (e) => {
      const newType = e.target.value;
      onOpenTypeChange(newType);
      setScreenName("");
      setHomeType("store");
      setHomeParams({});
    },
    [onOpenTypeChange]
  );

  // --- 스크린 모드 ---
  const handleScreenNameChange = useCallback(
    (e) => {
      const name = e.target.value;
      setScreenName(name);
      const url = name ? `fever://${name}` : "";
      onUrlChange(url, url);
    },
    [onUrlChange]
  );

  // --- 홈 모드 ---
  const handleHomeTypeChange = useCallback(
    (e) => {
      const newHomeType = e.target.value;
      setHomeType(newHomeType);
      setHomeParams({});
      onUrlChange("", "");
    },
    [onUrlChange]
  );

  const handleHomeParamChange = useCallback(
    (newParams) => {
      setHomeParams(newParams);
      const url = generateHomeUrl(homeType, newParams);
      onUrlChange(url, url);
    },
    [homeType, onUrlChange]
  );

  // 홈 미리보기 URL
  const homePreviewUrl = generateHomeUrl(homeType, homeParams);

  // --- 렌더링 ---
  const renderUrlInput = () => {
    switch (openType) {
      // 웹뷰 / 브라우저 / 수동처리: 기존 Android/iOS URL 입력
      case "W":
      case "B":
      case "N":
      default:
        return (
          <Box sx={{ mb: 2, width: "100%" }}>
            <DefaultLabel name={urlLabel} />
            <OutlinedInputRightAdornment
              value={linkUrl}
              onChange={(e) => onUrlChange(e.target.value, linkUrlI)}
              name={"Android"}
              adorment={"Android"}
              krName={"Android"}
              disabled={disabled}
            />
            <Grid sx={{ mt: 0.625, mb: 1.25 }}></Grid>
            <OutlinedInputRightAdornment
              value={linkUrlI}
              onChange={(e) => onUrlChange(linkUrl, e.target.value)}
              name={"IOS"}
              adorment={"IOS"}
              krName={"IOS"}
              disabled={disabled}
            />
          </Box>
        );

      // 스크린: fever:// 접두사 + ScreenName 단일 입력
      case "S":
        return (
          <Box sx={{ mb: 2, width: "100%" }}>
            <DefaultLabel name="스크린명" />
            <OutlinedInput
              value={screenName}
              onChange={handleScreenNameChange}
              sx={{
                height: 41,
                m: 0,
                fontSize: 12,
                backgroundColor: "white",
              }}
              disabled={disabled}
              fullWidth
              startAdornment={
                <InputAdornment position="start">
                  <Typography
                    variant="body2"
                    color="#9A9A9B"
                    fontWeight={500}
                  >
                    fever://
                  </Typography>
                </InputAdornment>
              }
              placeholder="ScreenName"
            />
            {screenName ? (
              <Typography
                variant="caption"
                color="#9A9A9B"
                sx={{ mt: 0.5, display: "block" }}
              >
                {`fever://${screenName}`}
              </Typography>
            ) : null}
          </Box>
        );

      // 홈: 하위 타입 드롭다운 + 파라미터 입력
      case "H":
        return (
          <Box sx={{ mb: 2, width: "100%" }}>
            <DefaultLabel name="홈 타입" />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Select
                size="small"
                sx={{ height: 41 }}
                value={homeType}
                onChange={handleHomeTypeChange}
                disabled={disabled}
              >
                {HomeType.map((item) => (
                  <MenuItem value={item.id} key={item.id}>
                    <Typography
                      variant="body"
                      color="grey.900"
                      fontWeight={400}
                    >
                      {item.text}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {renderHomeParamInput()}
            {homePreviewUrl ? (
              <Typography
                variant="caption"
                color="#9A9A9B"
                sx={{ mt: 1, display: "block", wordBreak: "break-all" }}
              >
                {homePreviewUrl}
              </Typography>
            ) : null}
          </Box>
        );
    }
  };

  const renderHomeParamInput = () => {
    switch (homeType) {
      case "store":
        return (
          <OutlinedInput
            value={homeParams.productNo || ""}
            onChange={(e) =>
              handleHomeParamChange({ ...homeParams, productNo: e.target.value })
            }
            sx={{
              height: 41,
              m: 0,
              fontSize: 12,
              backgroundColor: "white",
            }}
            disabled={disabled}
            fullWidth
            placeholder="상품번호"
            startAdornment={
              <InputAdornment position="start">
                <Typography
                  variant="body2"
                  color="#9A9A9B"
                  fontWeight={500}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  상품번호
                </Typography>
              </InputAdornment>
            }
          />
        );

      case "community_board":
        return (
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <OutlinedInput
                value={homeParams.board || ""}
                onChange={(e) =>
                  handleHomeParamChange({ ...homeParams, board: e.target.value })
                }
                sx={{
                  height: 41,
                  m: 0,
                  fontSize: 12,
                  backgroundColor: "white",
                }}
                disabled={disabled}
                fullWidth
                placeholder="게시판 ID"
                startAdornment={
                  <InputAdornment position="start">
                    <Typography
                      variant="body2"
                      color="#9A9A9B"
                      fontWeight={500}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      게시판
                    </Typography>
                  </InputAdornment>
                }
              />
            </Grid>
            <Grid item xs={6}>
              <OutlinedInput
                value={homeParams.filter ?? "-1"}
                onChange={(e) =>
                  handleHomeParamChange({ ...homeParams, filter: e.target.value })
                }
                sx={{
                  height: 41,
                  m: 0,
                  fontSize: 12,
                  backgroundColor: "white",
                }}
                disabled={disabled}
                fullWidth
                placeholder="필터"
                startAdornment={
                  <InputAdornment position="start">
                    <Typography
                      variant="body2"
                      color="#9A9A9B"
                      fontWeight={500}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      필터
                    </Typography>
                  </InputAdornment>
                }
              />
            </Grid>
          </Grid>
        );

      case "community_post":
        return (
          <OutlinedInput
            value={homeParams.post || ""}
            onChange={(e) =>
              handleHomeParamChange({ ...homeParams, post: e.target.value })
            }
            sx={{
              height: 41,
              m: 0,
              fontSize: 12,
              backgroundColor: "white",
            }}
            disabled={disabled}
            fullWidth
            placeholder="게시글 ID"
            startAdornment={
              <InputAdornment position="start">
                <Typography
                  variant="body2"
                  color="#9A9A9B"
                  fontWeight={500}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  게시글
                </Typography>
              </InputAdornment>
            }
          />
        );

      default:
        return null;
    }
  };

  return (
    <Grid container sx={{ mb: 3 }}>
      {renderUrlInput()}
      {openTypeList?.length > 0 ? (
        <Grid sx={{ flexGrow: 1 }}>
          <Box sx={{ flex: "100%" }}>
            <DefaultLabel name="오픈 방식" />
            <FormControl fullWidth>
              <Select
                labelId="open_type"
                id="open_type"
                align="left"
                size="small"
                sx={{ height: 41 }}
                value={openType}
                onChange={handleOpenTypeChange}
                disabled={disabled}
              >
                {openTypeList.map((data) => (
                  <MenuItem value={data.id} key={data.id}>
                    <Typography
                      variant="body"
                      color="grey.900"
                      fontWeight={400}
                    >
                      {data.text}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default UrlInputByOpenType;
